import React, { useState, useMemo } from 'react';
import { DUMMY_BUMDES, DUMMY_PO_LIST } from './mocks/dplDummyData';

const PriorityBadge = ({ priority }) => {
  const map = { high: ['danger', 'Tinggi'], medium: ['warning', 'Sedang'], low: ['secondary', 'Rendah'] };
  const [color, label] = map[priority] || ['secondary', priority];
  return (
    <span className={`badge bg-${color} bg-opacity-10 text-${color} border border-${color} border-opacity-10 rounded-pill px-2 py-1 extra-small fw-bold`}>
      {label}
    </span>
  );
};

const Escalations = () => {
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('all');

  const allEscalations = useMemo(
    () =>
      DUMMY_PO_LIST.flatMap((po) =>
        po.escalations.map((e) => ({ ...e, poName: po.name, poAvatar: po.avatar, poColor: po.color, university: po.university }))
      ),
    []
  );

  const filtered = useMemo(() => {
    let list = allEscalations;
    if (query) list = list.filter((e) =>
      e.poName.toLowerCase().includes(query.toLowerCase()) ||
      e.issue.toLowerCase().includes(query.toLowerCase())
    );
    if (filter !== 'all') list = list.filter((e) => e.priority === filter);
    return list;
  }, [query, filter, allEscalations]);

  const countByPriority = (p) => allEscalations.filter((e) => e.priority === p).length;

  return (
    <div className='card card-body border-0 shadow-sm rounded-4 mb-5'>
      {/* ── Page Header ── */}
      <div className='d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3 mb-4'>
        <div className='d-flex align-items-center gap-3'>
          <div className='bg-danger bg-opacity-10 d-flex align-items-center justify-content-center rounded-3' style={{ width: 40, height: 40 }}>
            <i className='bi bi-megaphone-fill text-danger fs-5'></i>
          </div>
          <div>
            <h1 className='h5 fw-bold mb-0 text-dark'>Eskalasi dari Project Officer</h1>
            <p className='text-muted mb-0 d-none d-lg-block' style={{ fontSize: '0.75rem' }}>
              Daftar masalah dari PO yang bertugas di BUMDes <strong>{DUMMY_BUMDES.name}</strong>.
            </p>
          </div>
        </div>
        <span className='badge bg-primary bg-opacity-10 text-primary px-3 py-2 rounded-pill fw-semibold align-self-start' style={{ fontSize: '0.75rem' }}>
          <i className='bi bi-eye me-1'></i>View Only
        </span>
      </div>

      {/* ── Stat Widgets Row ── */}
      <div className='row g-3 mb-4'>
        {[
          { label: 'Total Eskalasi', value: allEscalations.length, icon: 'bi-megaphone-fill', color: '#4f46e5' },
          { label: 'Prioritas Tinggi', value: countByPriority('high'), icon: 'bi-exclamation-octagon-fill', color: '#ef4444' },
          { label: 'Prioritas Sedang', value: countByPriority('medium'), icon: 'bi-exclamation-triangle-fill', color: '#f59e0b' },
          { label: 'Prioritas Rendah', value: countByPriority('low'), icon: 'bi-info-circle-fill', color: '#6b7280' },
        ].map((s) => (
          <div className='col-6 col-md-3' key={s.label}>
            <div className='bg-light rounded-4 p-3 d-flex align-items-center gap-3 border border-light-subtle'>
              <div className='rounded-3 d-flex align-items-center justify-content-center flex-shrink-0' style={{ width: 40, height: 40, background: `${s.color}15` }}>
                <i className={`bi ${s.icon} fs-5`} style={{ color: s.color }}></i>
              </div>
              <div>
                <div className='fw-bold fs-5 text-dark mb-0'>{s.value}</div>
                <div className='text-muted extra-small fw-semibold'>{s.label}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ── Warning Alert ── */}
      {countByPriority('high') > 0 && (
        <div className='alert alert-warning border-0 rounded-4 d-flex align-items-start gap-3 mb-4 shadow-sm'>
          <i className='bi bi-exclamation-triangle-fill text-warning mt-1 flex-shrink-0'></i>
          <div className='small'>
            Terdapat <strong>{countByPriority('high')} eskalasi prioritas tinggi</strong> yang membutuhkan respons segera dari Anda.
            Fitur membalas eskalasi secara langsung akan tersedia di pembaruan berikutnya.
          </div>
        </div>
      )}

      {/* ── Table Header / Summary ── */}
      <div className='d-flex align-items-center justify-content-between mb-3 mt-2'>
        <span className='fw-bold text-dark small'>Rincian Kendala & Solusi</span>
        <span className='text-muted extra-small'>{filtered.length} dari {allEscalations.length} eskalasi</span>
      </div>

      {/* ── Filter Toolbar ── */}
      <div className='row g-3 mb-4 align-items-center'>
        <div className='col-md-6 col-lg-7'>
          <div className='input-group input-group-sm bg-light rounded-pill px-3 py-2 border-0 shadow-none'>
            <span className='input-group-text bg-transparent border-0 text-muted'><i className='bi bi-search'></i></span>
            <input type='text' className='form-control bg-transparent border-0 shadow-none' placeholder='Cari nama PO atau isi eskalasi...' value={query} onChange={(e) => setQuery(e.target.value)} style={{ fontSize: '0.85rem' }} />
          </div>
        </div>
        <div className='col-md-6 col-lg-5 d-flex gap-2 flex-wrap justify-content-md-end'>
          {[
            { key: 'all', label: 'Semua' },
            { key: 'high', label: 'Tinggi' },
            { key: 'medium', label: 'Sedang' },
            { key: 'low', label: 'Rendah' },
          ].map((f) => (
            <button key={f.key} onClick={() => setFilter(f.key)}
              className={`btn btn-sm rounded-pill px-3 fw-medium ${filter === f.key ? 'btn-primary shadow-sm' : 'btn-light border border-light-subtle'}`}
              style={{ fontSize: '0.8rem' }}>
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Table ── */}
      <div className='table-responsive'>
        <table className='table table-hover align-middle mb-0'>
          <thead className='bg-light text-muted small text-uppercase fw-bold'>
            <tr>
              <th className='ps-4 py-3 border-0' style={{ width: 40 }}>#</th>
              <th className='border-0 py-3' style={{ width: 80 }}>ID</th>
              <th className='border-0 py-3'>Project Officer</th>
              <th className='border-0 py-3'>Isi Kendala / Masalah</th>
              <th className='border-0 py-3 text-center'>Prioritas</th>
              <th className='border-0 py-3 text-end pe-4'>Tanggal Eskalasi</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length > 0 ? (
              filtered.map((e, idx) => (
                <tr key={e.id} className='cursor-pointer transition-all'>
                  <td className='ps-4 py-3 text-muted small'>{idx + 1}</td>
                  <td className='py-3'>
                    <span className='badge bg-danger bg-opacity-10 text-danger border border-danger border-opacity-10 rounded-pill px-2 py-1 extra-small fw-bold'>{e.id}</span>
                  </td>
                  <td className='py-3'>
                    <div className='d-flex align-items-center gap-2'>
                      <div className='rounded-circle d-flex align-items-center justify-content-center text-white fw-bold flex-shrink-0' style={{ width: 30, height: 30, background: e.poColor, fontSize: '0.75rem' }}>{e.poAvatar}</div>
                      <div>
                        <div className='fw-bold text-dark small'>{e.poName}</div>
                        <div className='text-muted extra-small d-flex align-items-center gap-1'>
                          <i className='bi bi-building text-primary opacity-50'></i>{e.university}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className='py-3' style={{ maxWidth: 300 }}>
                    <div className='small text-dark' style={{ lineHeight: 1.5 }}>{e.issue}</div>
                  </td>
                  <td className='py-3 text-center'><PriorityBadge priority={e.priority} /></td>
                  <td className='py-3 text-end pe-4'>
                    <div className='small text-muted d-flex align-items-center justify-content-end gap-1'>
                      <i className='bi bi-calendar3 text-primary opacity-50'></i>{e.date}
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr><td colSpan={6} className='text-center py-5'>
                <div className='text-muted mb-3'>
                  <i className={`bi ${allEscalations.length === 0 ? 'bi-check-circle-fill text-success' : 'bi-megaphone'} fs-1 opacity-25`}></i>
                </div>
                <h6 className='fw-bold text-dark mb-1'>{allEscalations.length === 0 ? 'Tidak Ada Eskalasi' : 'Tidak Ada Data'}</h6>
                <p className='text-muted small mb-0'>
                  {allEscalations.length === 0 ? 'Semua PO binaan tidak memiliki eskalasi yang perlu ditangani.' : 'Tidak ada eskalasi yang sesuai dengan filter yang dipilih.'}
                </p>
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      <style>{`
        .transition-all { transition: all 0.2s ease; }
        .cursor-pointer { cursor: pointer; }
        .extra-small { font-size: 0.72rem; }
        .table-hover tbody tr:hover { background-color: rgba(var(--bs-danger-rgb), 0.03) !important; transform: scale(0.998); }
      `}</style>
    </div>
  );
};

export default Escalations;
